import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Clock, User, Save, Zap } from 'lucide-react';
import { format, addHours } from 'date-fns';
import { Employee, DayShift } from '@/types';
import { ShiftTemplate } from '@/components/shifts/ShiftTemplateItem';
import { useEmployeeAvailability } from '@/hooks/employees/useEmployeeAvailability';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';

interface ShiftBuilderDialogProps {
  open: boolean;
  startTime: Date;
  predefinedEndTime?: Date; // Optional predefined end time
  onSave: (endTime: Date, title: string, employeeId?: string) => void;
  onCancel: () => void;
  employees: Employee[];
  templates: ShiftTemplate[];
  recentTemplates: ShiftTemplate[];
  allDayShifts?: DayShift[];
  businessTimezone?: string; // Add timezone prop
  businessCountryCode?: string; // Add country code for timezone fallback
}

const ShiftBuilderDialog: React.FC<ShiftBuilderDialogProps> = ({
  open,
  startTime,
  predefinedEndTime,
  onSave,
  onCancel,
  employees,
  templates,
  recentTemplates,
  allDayShifts = [],
  businessTimezone,
  businessCountryCode
}) => {
  // Determine business timezone - use provided timezone or browser timezone (no country fallback)
  const resolvedTimezone = businessTimezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
  // Calculate initial duration from predefined end time or default to 8 hours
  const initialDuration = predefinedEndTime 
    ? ((predefinedEndTime.getTime() - startTime.getTime()) / (1000 * 60 * 60)).toString()
    : '8';
    
  const [duration, setDuration] = useState(initialDuration);
  const [title, setTitle] = useState('');
  const [selectedEmployee, setSelectedEmployee] = useState('assign_later');
  const [selectedTemplate, setSelectedTemplate] = useState('none');

  // Calculate end time for availability checking - use predefined if available
  const proposedEndTime = predefinedEndTime || (duration ? addHours(startTime, parseFloat(duration)) : null);

  // Use the availability hook
  const { isEmployeeAvailable, getEmployeeConflicts } = useEmployeeAvailability({
    employees,
    proposedStartTime: startTime,
    proposedEndTime,
    existingShifts: allDayShifts,
    excludeShiftId: undefined
  });

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      setDuration(initialDuration);
      setTitle('');
      setSelectedEmployee('assign_later');
      setSelectedTemplate('none');
    }
  }, [open, initialDuration]);

  // Update title and duration when template is selected
  useEffect(() => {
    if (selectedTemplate && selectedTemplate !== 'none') {
      const template = [...templates, ...recentTemplates].find(t => t.id === selectedTemplate);
      if (template) {
        setDuration((template.duration / 60).toString());
        setTitle(template.name);
      }
    }
  }, [selectedTemplate, templates, recentTemplates]);

  const handleSave = () => {
    // Use predefined end time if available, otherwise calculate from duration
    const endTime = predefinedEndTime || addHours(startTime, parseFloat(duration));
    onSave(endTime, title || 'Shift', selectedEmployee === 'assign_later' ? undefined : selectedEmployee || undefined);
  };


  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onCancel()}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5 text-blue-600" />
            Create Shift - {format(startTime, 'HH:mm')}
            {predefinedEndTime && ` to ${format(predefinedEndTime, 'HH:mm')}`}
          </DialogTitle>
          <DialogDescription>
            Create a new shift by selecting duration, title, and optionally assigning an employee.
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">


          {/* Recent Templates */}
          {recentTemplates.length > 0 && (
            <div>
              <div className="flex items-center gap-1 text-sm font-medium mb-2">
                <Zap className="h-4 w-4" />
                Recent Templates:
              </div>
              <div className="flex flex-wrap gap-2">
                {recentTemplates.slice(0, 4).map((template) => (
                  <Button
                    key={template.id}
                    variant="outline"
                    size="sm"
                    className="h-8 text-xs"
                    onClick={() => {
                      setSelectedTemplate(template.id);
                      setDuration((template.duration / 60).toString());
                      setTitle(template.name);
                      handleSave();
                    }}
                  >
                    {template.name} ({Math.floor(template.duration / 60)}h)
                  </Button>
                ))}
              </div>
            </div>
          )}

          {/* Custom Shift Form */}
          <div className="space-y-4 pt-4 border-t">
            <h4 className="text-sm font-medium">Custom Shift Details:</h4>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Duration (hours)</label>
                <Input
                  type="number"
                  step="0.5"
                  min="0.5"
                  max="24"
                  value={duration}
                  onChange={(e) => setDuration(e.target.value)}
                  placeholder="8"
                  disabled={!!predefinedEndTime} // Disable if end time is predefined
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Title</label>
                <Input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Shift title"
                  autoFocus
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium flex items-center gap-1">
                <User className="h-4 w-4" />
                Employee
              </label>
              <Select value={selectedEmployee} onValueChange={setSelectedEmployee}>
                <SelectTrigger>
                  <SelectValue placeholder="Assign later" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="assign_later">Assign later</SelectItem>
                  {employees.map((employee) => {
                    const available = isEmployeeAvailable(employee.id);
                    const conflicts = getEmployeeConflicts(employee.id);
                    
                    return (
                      <SelectItem
                        key={employee.id}
                        value={employee.id}
                        className={available ? "" : "text-red-600"}
                      >
                        {/* Note: TooltipProvider is hoisted to ComposedShiftProvider */}
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="flex items-center gap-2 w-full">
                              <div className={`w-2 h-2 ${available ? 'bg-green-500' : 'bg-red-500'} rounded-full`}></div>
                              {employee.name}
                            </span>
                          </TooltipTrigger>
                          {!available && conflicts && conflicts.length > 0 && (
                            <TooltipContent>
                              <p className="text-sm">Conflicts with:</p>
                              {conflicts.map((conflict, idx) => (
                                <p key={idx} className="text-xs">
                                  {conflict.title || 'Shift'} ({format(conflict.startTime, 'h:mm a')} - {format(conflict.endTime, 'h:mm a')})
                                </p>
                              ))}
                            </TooltipContent>
                          )}
                        </Tooltip>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Template</label>
              <Select value={selectedTemplate} onValueChange={setSelectedTemplate}>
                <SelectTrigger>
                  <SelectValue placeholder="None" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {templates.map((template) => (
                    <SelectItem key={template.id} value={template.id}>
                      {template.name} ({Math.floor(template.duration / 60)}h)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button 
            onClick={onCancel}
            variant="outline"
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSave}
            disabled={!duration}
          >
            <Save className="h-4 w-4 mr-2" />
            Create Shift
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ShiftBuilderDialog;